import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang, roomsLabel } from "../i18n";

export const HouseGallery = ({ house, onClose }) => {
  const [i, setI] = useState(0);
  const { t } = useLang();
  const n = house.photos.length;
  const go = (d) => setI((v) => (v + d + n) % n);

  useEffect(() => {
    document.querySelector(`[data-testid=house-gallery-thumb-${i}]`)?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [i]);

  useEffect(() => {
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((v) => (v + 1) % n);
      if (e.key === "ArrowLeft") setI((v) => (v - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [n, onClose]);

  return (
    <motion.div
      data-testid={`house-gallery-${house.id}`}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-[#1C1D1A]/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-5 md:px-10 py-5 text-[#F7F5F0]" onClick={(e) => e.stopPropagation()}>
        <div>
          <p className="eyebrow text-[10px] text-[#D8C7B5]">{roomsLabel(house.roomsN, t)} · {house.guests} {t.houses.guests}</p>
          <h3 className="font-display text-2xl md:text-4xl mt-1">{house.name}</h3>
        </div>
        <div className="flex items-center gap-4">
          <span data-testid="house-gallery-counter" className="hidden sm:inline font-mono text-xs text-[#D8C7B5]">{String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>
          <button data-testid="house-gallery-close" onClick={onClose} aria-label={t.gallery.close} className="w-11 h-11 rounded-full border border-[#F7F5F0]/40 flex items-center justify-center hover:bg-[#F7F5F0] hover:text-[#1C1D1A] transition-colors"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 mx-5 md:mx-24" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence initial={false}>
          <motion.img
            key={house.photos[i]}
            src={house.photos[i]}
            alt={`${house.name} — foto ${i + 1}`}
            initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </AnimatePresence>
        <button data-testid="house-gallery-prev" onClick={() => go(-1)} aria-label={t.gallery.prev} className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#1C1D1A]/60 border border-[#F7F5F0]/40 text-[#F7F5F0] flex items-center justify-center hover:bg-[#F7F5F0] hover:text-[#1C1D1A] transition-colors"><ChevronLeft /></button>
        <button data-testid="house-gallery-next" onClick={() => go(1)} aria-label={t.gallery.next} className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#1C1D1A]/60 border border-[#F7F5F0]/40 text-[#F7F5F0] flex items-center justify-center hover:bg-[#F7F5F0] hover:text-[#1C1D1A] transition-colors"><ChevronRight /></button>
      </div>

      <div className="flex gap-2 md:gap-3 justify-start overflow-x-auto px-5 md:px-10 py-5 [&>*:first-child]:ml-auto [&>*:last-child]:mr-auto" onClick={(e) => e.stopPropagation()}>
        {house.photos.map((p, k) => (
          <button
            key={p}
            data-testid={`house-gallery-thumb-${k}`}
            onClick={() => setI(k)}
            className={`shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-[opacity,border-color] ${k === i ? "border-[#E8B49A] opacity-100" : "border-transparent opacity-50 hover:opacity-90"}`}
          >
            <img src={p} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </motion.div>
  );
};
